/**
 * RFQ Product Overview Dashboard Component
 * 
 * Displays detailed product information for RFQ request with editable fields.
 * Shows products in card format with images on the right and editable fields on the left.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getCategoryById } from '../../../../utils/categories';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import './RFQProductOverviewDashboard.scss';

interface RFQProductOverviewDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const RFQProductOverviewDashboard: React.FC<RFQProductOverviewDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const location = useLocation();

  // Navigation actions
  const navInfo = useMemo(() => getRFQNavigationInfo(currentRoute), [currentRoute]);
  const navigationActions = useMemo(() => {
    const hasPreviousPage = navInfo?.previousPage;
    const hasNextPage = navInfo?.nextPage;

    if (!hasPreviousPage && !hasNextPage) {
      return undefined;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasPreviousPage && navInfo?.previousPage && (
            <button
              onClick={() => {
                if (navInfo && navInfo.previousPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage));
                }
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              ← Back to {getRFQPageName(navInfo.previousPage)}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {hasNextPage && navInfo?.nextPage && (
            <button
              onClick={() => {
                if (navInfo.nextPage) {
                  onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage));
                }
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Next to {navInfo.nextPage ? getRFQPageName(navInfo.nextPage) : ''} →
            </button>
          )}
        </div>
      </div>
    );
  }, [navInfo, onNavigate]);

  // Extract requestId from URL path
  // Pattern: /dashboard/rfqs/open/:requestId/products or /dashboard/user/rfq/:requestId/products
  const requestId = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const openIndex = pathParts.indexOf('open');
    const rfqIndex = pathParts.indexOf('rfq'); // For /dashboard/user/rfq/ pattern
    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      return pathParts[openIndex + 1];
    } else if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      return pathParts[rfqIndex + 1];
    }
    return null;
  }, [location.pathname]);

  // Determine base route and if this is basic plan user route
  const isBasicPlanRoute = location.pathname.startsWith('/dashboard/user/rfq/');
  const baseRoute = isBasicPlanRoute ? '/dashboard/user/rfq' : '/dashboard/rfqs/open';

  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  console.log('🎨 RFQProductOverviewDashboard mounted');
  console.log('🆔 Request ID:', requestId);
  console.log('🔑 Token exists:', !!token);

  // Load products
  useEffect(() => {
    console.log('🔄 useEffect triggered - requestId:', requestId, 'token:', !!token);
    if (!requestId || !token) {
      console.log('⚠️ Missing requestId or token, skipping fetch');
      return;
    }

    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log('🔍 Fetching products for request:', requestId);
        console.log('🔗 API URL:', API_ENDPOINTS.REQUEST_V2_PRODUCTS(requestId));

        const response = await fetch(API_ENDPOINTS.REQUEST_V2_PRODUCTS(requestId), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Products API response:', data);
        console.log('📊 Products data:', data.data);
        console.log('🔢 Products count:', data.data?.length);

        // Debug: Log full product objects
        if (data.data && data.data.length > 0) {
          console.log('🔍 Full first product:', JSON.stringify(data.data[0], null, 2));
        }

        if (data.success && data.data) {
          console.log('💾 Setting products state with:', data.data.length, 'items');
          setProducts(data.data);
        } else {
          console.log('❌ Response not successful or no data');
          throw new Error(data.message || 'Failed to load products');
        }
      } catch (error: any) {
        console.error('Error fetching products:', error);
        setError(error.message || 'Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [requestId, token]);


  const handleEdit = (productId: string) => {
    setEditingProductId(productId);
    const product = products.find(p => p._id === productId);
    if (product) {
      setEditedData({ ...product });
    }
  };

  const handleCancel = () => {
    setEditingProductId(null);
    setEditedData({});
  };

  const handleSave = async (productId: string) => {
    try {
      console.log('💾 Saving product:', productId, editedData);

      // Construct the full URL manually
      const updateUrl = `${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products/${productId}`;
      console.log('🔗 Update URL:', updateUrl);

      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedData)
      });

      if (!response.ok) {
        throw new Error('Failed to update product');
      }

      const data = await response.json();
      if (data.success) {
        // Update local state
        setProducts(products.map(p => p._id === productId ? { ...p, ...editedData } : p));
        setEditingProductId(null);
        setEditedData({});
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      alert('Failed to update product: ' + error.message);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleToggleProduct = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSplitRFQ = async () => {
    if (selectedProductIds.length === 0) {
      alert('Please select at least one product to split');
      return;
    }

    if (selectedProductIds.length === products.length) {
      alert('You cannot split all products. At least one product must remain in the original RFQ.');
      return;
    }

    if (!window.confirm(`Split ${selectedProductIds.length} product(s) into a new RFQ?`)) {
      return;
    }

    try {
      console.log('🔀 Splitting RFQ with products:', selectedProductIds);

      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/split`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedProductIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to split RFQ');
      }

      const data = await response.json();
      if (data.success) {
        alert(`RFQ split successfully! New RFQ ID: ${data.data.new_request_id}`);

        // Refresh products list
        window.location.reload();
      } else {
        throw new Error(data.message || 'Failed to split RFQ');
      }
    } catch (error: any) {
      console.error('Error splitting RFQ:', error);
      alert('Failed to split RFQ: ' + error.message);
    }
  };

  // Secondary sidebar component
  // Loading state
  if (isLoading) {
    const Layout = isBasicPlanRoute ? UserLayout : AdminLayout;
    return (
      <Layout
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="Loading Products..."
        getDynamicSubItems={getDynamicSubItems}
        navigationActions={navigationActions}
      >
        <div className="loading-state" style={{ padding: '2rem' }}>
          <p>Loading products...</p>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    const Layout = isBasicPlanRoute ? UserLayout : AdminLayout;
    return (
      <Layout
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="Error"
        getDynamicSubItems={getDynamicSubItems}
        navigationActions={navigationActions}
      >
        <div className="error-state" style={{ padding: '2rem' }}>
          <h2>Error Loading Products</h2>
          <p>{error}</p>
          <button
            onClick={() => requestId && onNavigate(`${baseRoute}/${requestId}`)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            ← Back to RFQ Details
          </button>
        </div>
      </Layout>
    );
  }

  const Layout = isBasicPlanRoute ? UserLayout : AdminLayout;

  return (
    <Layout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle="Product Overview"
      pageDescription="Detailed view of all products in this RFQ request"
      getDynamicSubItems={getDynamicSubItems}
      navigationActions={navigationActions}
    >
      <div className="rfq-product-overview-container" style={{
        width: '100%',
        maxWidth: '100%',
        padding: '1.5rem'
      }}>
        {/* Header */}
        <div className="overview-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 style={{ margin: 0 }}>Product Overview</h1>
            {selectedProductIds.length > 0 && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {selectedProductIds.length} selected
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selectedProductIds.length > 0 && products.length > 1 && (
              <button
                onClick={handleSplitRFQ}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              >
                🔀 Open as New RFQ ({selectedProductIds.length})
              </button>
            )}

          </div>
        </div>

        {/* Products List */}
        {products.length === 0 ? (
          <div className="empty-state">
            <p>No products found in this request</p>
          </div>
        ) : (
          <div className="products-list" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            width: '100%',
            maxWidth: '100%'
          }}>
            {products.map((product, index) => {
              console.log('🖼️ Product image data:', {
                _id: product._id,
                name: product.product_name,
                image_gridfs_id: product.image_gridfs_id,
                image_url: product.image_url
              });

              const isEditing = editingProductId === product._id;
              const displayData = isEditing ? editedData : product;

              return (
                <div key={product._id || index} className="product-card" style={{
                  width: '100%',
                  maxWidth: '100%',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}>
                  {/* Product Header */}
                  <div className="product-card-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.25rem 1.5rem',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {products.length > 1 && (
                        <input
                          type="checkbox"
                          checked={selectedProductIds.includes(product._id)}
                          onChange={() => handleToggleProduct(product._id)}
                          disabled={isEditing}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: isEditing ? 'not-allowed' : 'pointer'
                          }}
                        />
                      )}
                      <h3 style={{ margin: 0 }}>Product #{product.line_number || index + 1}</h3>
                    </div>
                    <div className="product-actions">
                      {isEditing ? (
                        <>
                          <button onClick={() => handleSave(product._id)} className="save-button">
                            Save
                          </button>
                          <button onClick={handleCancel} className="cancel-button">
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button onClick={() => handleEdit(product._id)} className="edit-button">
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Product Content */}
                  <div className="product-card-content" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 600px',
                    gap: '2rem',
                    padding: '1.5rem'
                  }}>
                    {/* Left Side - Editable Fields */}
                    <div className="product-fields">
                      <div className="field-group">
                        <label>Product Name</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.product_name || ''}
                            onChange={(e) => handleFieldChange('product_name', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.product_name || '-'}</div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Brand</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.product_brand || ''}
                            onChange={(e) => handleFieldChange('product_brand', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.product_brand || '-'}</div>
                        )}
                      </div>

                      <div className="field-row">
                        <div className="field-group">
                          <label>Quantity</label>
                          {isEditing ? (
                            <input
                              type="number"
                              value={displayData.quantity || ''}
                              onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value))}
                            />
                          ) : (
                            <div className="field-value">{displayData.quantity || '-'}</div>
                          )}
                        </div>

                        <div className="field-group">
                          <label>Unit</label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={displayData.unit || displayData.base_unit || ''}
                              onChange={(e) => handleFieldChange('unit', e.target.value)}
                            />
                          ) : (
                            <div className="field-value">{displayData.unit || displayData.base_unit || '-'}</div>
                          )}
                        </div>
                      </div>

                      <div className="field-row">
                        <div className="field-group">
                          <label>Budget</label>
                          {isEditing ? (
                            <input
                              type="number"
                              value={displayData.budget || ''}
                              onChange={(e) => handleFieldChange('budget', parseFloat(e.target.value))}
                            />
                          ) : (
                            <div className="field-value">{displayData.budget || '-'}</div>
                          )}
                        </div>

                        <div className="field-group">
                          <label>Currency</label>
                          {isEditing ? (
                            <select
                              value={displayData.currency || 'TRY'}
                              onChange={(e) => handleFieldChange('currency', e.target.value)}
                            >
                              <option value="TRY">TRY</option>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="CHF">CHF</option>
                            </select>
                          ) : (
                            <div className="field-value">{displayData.currency || 'TRY'}</div>
                          )}
                        </div>
                      </div>

                      <div className="field-group">
                        <label>Category</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.category || ''}
                            onChange={(e) => handleFieldChange('category', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">
                            {(() => {
                              if (!displayData.category) return '-';

                              // Handle array of categories
                              const categories = Array.isArray(displayData.category)
                                ? displayData.category
                                : displayData.category.split(',').map((c: string) => c.trim());

                              // Map UNSPSC codes to names
                              const mappedNames = categories.map((code: string) => {
                                const category = getCategoryById(code);
                                return category?.name.tr || code;
                              });

                              return mappedNames.join(', ');
                            })()}
                          </div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Global Code</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.product_global_code || ''}
                            onChange={(e) => handleFieldChange('product_global_code', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.product_global_code || '-'}</div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Amazon ASIN</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.amazon_asin || ''}
                            onChange={(e) => handleFieldChange('amazon_asin', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.amazon_asin || '-'}</div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Dimensions</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.dimensions || ''}
                            onChange={(e) => handleFieldChange('dimensions', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.dimensions || '-'}</div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Color</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayData.color || ''}
                            onChange={(e) => handleFieldChange('color', e.target.value)}
                          />
                        ) : (
                          <div className="field-value">{displayData.color || '-'}</div>
                        )}
                      </div>

                      <div className="field-group full-width">
                        <label>Description</label>
                        {isEditing ? (
                          <textarea
                            value={displayData.description || ''}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            rows={3}
                          />
                        ) : (
                          <div className="field-value">{displayData.description || '-'}</div>
                        )}
                      </div>

                      <div className="field-group">
                        <label>Last Brave Search</label>
                        <div className="field-value">
                          {product.last_search_date
                            ? new Date(product.last_search_date).toLocaleString('tr-TR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                            : 'Never'}
                        </div>
                      </div>

                      <div className="field-group full-width">
                        <label>Notes</label>
                        {isEditing ? (
                          <textarea
                            value={displayData.notes || ''}
                            onChange={(e) => handleFieldChange('notes', e.target.value)}
                            rows={2}
                          />
                        ) : (
                          <div className="field-value">{displayData.notes || '-'}</div>
                        )}
                      </div>
                    </div>

                    {/* Right Side - Image */}
                    <div className="product-image" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem',
                      minHeight: '300px',
                      minWidth: '600px',
                      width: '600px',
                      flexShrink: 0
                    }}>
                      {displayData.image_gridfs_id ? (
                        <img
                          src={`http://localhost:5001/api/gridfs/${displayData.image_gridfs_id}`}
                          alt={displayData.product_name || 'Product'}
                          style={{
                            width: '580px',
                            height: 'auto',
                            maxHeight: '350px',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                          onError={(e) => {
                            console.log('❌ Image load error for:', displayData.image_gridfs_id);
                            e.currentTarget.style.display = 'none';
                            const noImageDiv = e.currentTarget.parentElement?.querySelector('.no-image-fallback');
                            if (noImageDiv) {
                              (noImageDiv as HTMLElement).style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            console.log('✅ Image loaded successfully:', displayData.image_gridfs_id);
                            console.log('📐 Image natural size:', e.currentTarget.naturalWidth, 'x', e.currentTarget.naturalHeight);
                            console.log('📐 Image displayed size:', e.currentTarget.width, 'x', e.currentTarget.height);
                            console.log('📐 Image parent width:', e.currentTarget.parentElement?.offsetWidth);
                          }}
                        />
                      ) : null}
                      <div
                        className="no-image-fallback"
                        style={{
                          display: displayData.image_gridfs_id ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          color: '#9ca3af'
                        }}
                      >
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                          No Image Available
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RFQProductOverviewDashboard;

